import { useState, useRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import { HistoryMessage } from '@/components/common/ChatHistoryModal';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  LinearProgress,
  Alert,
  Chip,
  IconButton,
  Card,
  CardContent,
  Avatar,
  Collapse,
  Tooltip,
  Stack,
  Divider,
  Snackbar,
} from '@mui/material';
import TextGenerationModals from './TextGenerationModals';
import { Refresh, Translate, ContentCopy } from '@mui/icons-material';
import { keyframes } from '@mui/system';
import aiIcon from '@/assets/icons/ai.svg';
import copyIcon from '@/assets/icons/copy.svg';
import normalIcon from '@/assets/icons/normal.svg';
import PromptComposer from '@/components/common/PromptComposer';
import { streamKpcTextGeneration } from '@/utils/sse-utils';
import { uploadFile, validateFile, getFileIcon, formatFileSize } from '@/api/generated/text-generation.service';
import CharacterCounter from '@/components/common/CharacterCounter';

// 로딩 애니메이션 keyframes / Loading animation keyframes
const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
`;

const LoadingDots = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      minHeight: '1.5em', // 텍스트와 같은 최소 높이 설정
      padding: '0.5em 0', // 상하 패딩으로 텍스트와 비슷한 높이 유지
      '& > div': {
        width: 6,
        height: 6,
        borderRadius: '50%',
        backgroundColor: 'primary.main',
        animation: `${bounce} 1.4s infinite ease-in-out`,
        '&:nth-of-type(1)': { animationDelay: '-0.32s' },
        '&:nth-of-type(2)': { animationDelay: '-0.16s' },
        '&:nth-of-type(3)': { animationDelay: '0s' },
      },
    }}
  >
    <div />
    <div />
    <div />
  </Box>
);

interface ChatMessage extends HistoryMessage {
  // HistoryMessage를 확장하여 호환성 유지
}

interface SharedTabState {
  // 텍스트 생성 탭 상태 / Text generation tab state
  textPrompt: string;
  textTranslatedPrompt: string;
  textMessages: any[];
  textShowTranslation: boolean;

  // 이미지 생성 탭 상태 / Image generation tab state
  imagePrompt: string;
  imageMessages: any[];
  imageCopiedPrompts: any[];
  imageShowCopiedPrompts: boolean;
}

interface TextGenerationTabProps {
  hideInput?: boolean;
  sharedState: SharedTabState;
  setSharedState: React.Dispatch<React.SetStateAction<SharedTabState>>;
}

const TextGenerationTab = ({
  hideInput = false,
  sharedState,
  setSharedState,
}: TextGenerationTabProps) => {
  // 공유 상태에서 값 가져오기 / Get values from shared state
  const prompt = sharedState.textPrompt;
  const translatedPrompt = sharedState.textTranslatedPrompt;
  const messages = sharedState.textMessages;
  const showTranslation = sharedState.textShowTranslation;

  // 로컬 상태 / Local state
  const [isLoading, setIsLoading] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [isUploading, setIsUploading] = useState(false); // 파일 업로드 상태 / File upload state
  const [translatedByteCount, setTranslatedByteCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadedFileUrls, setUploadedFileUrls] = useState<string[]>([]);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [sseConnection, setSseConnection] = useState<any>(null);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null); // 현재 스트리밍 중인 메시지 ID
  const [snackbarOpen, setSnackbarOpen] = useState(false); // Snackbar 상태 / Snackbar state
  const [snackbarMessage, setSnackbarMessage] = useState(''); // Snackbar 메시지 / Snackbar message
  const [showCopySuccess, setShowCopySuccess] = useState(false); // 복사 성공 알림 상태 / Copy success notification state
  const [lastRequestData, setLastRequestData] = useState<{prompt: string, imageUrls: string[]} | null>(null); // 마지막 요청 데이터 저장 / Store last request data
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 상태 업데이트 함수들 / State update functions
  const setPrompt = (value: string) => {
    setSharedState(prev => ({ ...prev, textPrompt: value }));
  };

  const setTranslatedPrompt = (value: string) => {
    setSharedState(prev => ({ ...prev, textTranslatedPrompt: value }));
  };

  const setMessages = (value: React.SetStateAction<ChatMessage[]>) => {
    setSharedState(prev => ({
      ...prev,
      textMessages:
        typeof value === 'function' ? value(prev.textMessages) : value,
    }));
  };

  const setShowTranslation = (value: boolean) => {
    setSharedState(prev => ({ ...prev, textShowTranslation: value }));
  };

  // Snackbar 표시 함수 / Show snackbar function
  const showSnackbar = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  // 복사 성공 알림 닫기 / Close copy success notification
  const handleCloseCopySuccess = () => {
    setShowCopySuccess(false);
  };

  // 재생성 함수 / Regenerate function
  const handleRegenerate = async (messageId: string) => {
    if (!lastRequestData) {
      showSnackbar('재생성할 데이터가 없습니다.');
      return;
    }

    // 기존 AI 메시지 제거 / Remove existing AI message
    setMessages(prev => prev.filter(msg => msg.id !== messageId));

    // 새로운 AI 메시지 생성 / Create new AI message
    const newAiMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'ai',
      content: '',
      timestamp: new Date(),
    };

    // 새로운 AI 메시지 추가 / Add new AI message
    setMessages(prev => [...prev, newAiMessage]);
    setStreamingMessageId(newAiMessage.id);
    setIsLoading(true);
    setError(null);

    try {
      // 동일한 요청으로 재생성 / Regenerate with same request
      const connection = streamKpcTextGeneration(
        lastRequestData.prompt,
        lastRequestData.imageUrls,
        {
          onMessage: (data: string | any) => {
            // 스트리밍 데이터 처리 / Process streaming data
            let textContent = '';
            
            if (typeof data === 'string') {
              try {
                const parsed = JSON.parse(data);
                if (parsed.type === 'chunk' && parsed.content) {
                  textContent = parsed.content;
                } else if (parsed.type === 'start' || parsed.type === 'end') {
                  return;
                } else {
                  textContent = data;
                }
              } catch {
                textContent = data;
              }
            } else if (typeof data === 'object' && data !== null) {
              if (data.type === 'chunk' && data.content) {
                textContent = data.content;
              } else if (data.type === 'start' || data.type === 'end') {
                return;
              } else {
                textContent = JSON.stringify(data);
              }
            } else {
              textContent = String(data);
            }

            if (!textContent.trim()) {
              return;
            }
            
            // 스트리밍 데이터를 AI 메시지에 누적 / Accumulate streaming data to AI message
            flushSync(() => {
              setMessages(prev =>
                prev.map(msg =>
                  msg.id === newAiMessage.id 
                    ? { ...msg, content: msg.content + textContent } 
                    : msg
                )
              );
            });
            
            // 스트리밍 중일 때 스크롤을 하단으로 유지 / Keep scroll at bottom during streaming
            if (streamingMessageId === newAiMessage.id) {
              // DOM 업데이트 후 정확히 하단으로 스크롤
              // Scroll to bottom accurately after DOM update
              requestAnimationFrame(() => {
                scrollToBottom(false);
              });
            }
          },
          onError: (error: Event) => {
            console.error('SSE 스트리밍 오류 / SSE streaming error:', error);
            setError('AI 응답 생성 중 오류가 발생했습니다.');
            setIsLoading(false);
          },
          onComplete: () => {
            console.log('SSE 스트리밍 완료 / SSE streaming completed');
            setIsLoading(false);
            setStreamingMessageId(null);
          },
          onOpen: () => {
            console.log('SSE 스트리밍 시작 / SSE streaming started');
          },
        }
      );

      setSseConnection(connection);
    } catch (err) {
      console.error('재생성 오류 / Regenerate error:', err);
      setError('AI 응답 재생성 중 오류가 발생했습니다.');
      setIsLoading(false);
    }
  };

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const maxCharacters = 3000; // 최대 문자 수 / Maximum character count

  // 번역된 프롬프트 바이트 수 계산 / Calculate translated prompt byte count
  useEffect(() => {
    const bytes = new Blob([translatedPrompt]).size;
    setTranslatedByteCount(bytes);
  }, [translatedPrompt]);

  // 스크롤을 하단으로 이동하는 함수 / Function to scroll to bottom
  const scrollToBottom = (smooth = false) => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      // requestAnimationFrame을 사용하여 DOM 업데이트 후 정확히 스크롤
      // Use requestAnimationFrame to scroll accurately after DOM update
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // 이중 requestAnimationFrame으로 DOM 업데이트 완료를 확실히 기다림
          // Double requestAnimationFrame to ensure DOM update completion
          const scrollHeight = container.scrollHeight;
          const clientHeight = container.clientHeight;
          const maxScroll = scrollHeight - clientHeight;
          
          container.scrollTo({
            top: maxScroll > 0 ? maxScroll : scrollHeight,
            behavior: smooth ? 'smooth' : 'auto'
          });
        });
      });
    }
  };

  // 메시지 목록 자동 스크롤 / Auto scroll to bottom of messages
  useEffect(() => {
    if (messagesContainerRef.current && messagesEndRef.current) {
      const container = messagesContainerRef.current;
      // 더 정확한 하단 감지를 위해 threshold를 작게 설정
      // Set smaller threshold for more accurate bottom detection
      const threshold = 50;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - threshold;
      
      // 사용자가 하단 근처에 있을 때만 자동 스크롤
      // Auto scroll only when user is near bottom
      if (isNearBottom) {
        // DOM 업데이트를 기다린 후 스크롤
        // Wait for DOM update before scrolling
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (streamingMessageId) {
              // 스트리밍 중일 때는 즉시 스크롤 (흔들림 방지)
              // Immediate scroll during streaming (prevent jitter)
              scrollToBottom(false);
            } else {
              // 스트리밍이 완료된 후에만 부드러운 스크롤
              // Smooth scroll only after streaming completes
              scrollToBottom(true);
            }
          });
        });
      }
    }
  }, [messages, streamingMessageId]);

  // [Gemini/Imagen 전환] 번역 기능 주석 처리 - Gemini는 한국어를 잘 인식하므로 번역 불필요
  // const handleTranslate = async () => {
  //   if (!prompt.trim()) {
  //     setTranslatedPrompt('');
  //     return;
  //   }
  //   setIsTranslating(true);
  //   setError(null);
  //   try {
  //     await new Promise(resolve => setTimeout(resolve, 500));
  //     setTranslatedPrompt(`[Translated] ${prompt}`);
  //   } catch (err) {
  //     setError('번역 중 오류가 발생했습니다.');
  //   } finally {
  //     setIsTranslating(false);
  //   }
  // };

    // 프롬프트 전송 / Send prompt
    const handleSendPrompt = async () => {
      if (!prompt.trim() || isLoading) return;

      const userMessage: ChatMessage = {
        id: Date.now().toString(),
        type: 'user',
        content: prompt,
        timestamp: new Date(),
        translatedContent: translatedPrompt || undefined,
      };

      // AI 메시지 초기화 / Initialize AI message
      const aiMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: '',
        timestamp: new Date(),
      };

      // 사용자 메시지와 AI 메시지를 즉시 추가 / Add user and AI messages immediately
      setMessages(prev => [...prev, userMessage, aiMessage]);
      setStreamingMessageId(aiMessage.id); // 스트리밍 상태 설정
      setIsLoading(true);
      setError(null);

      // 요청 데이터 저장 (재생성용) / Store request data for regeneration
      setLastRequestData({
        prompt,
        imageUrls: uploadedFileUrls,
      });

           try {
             // SSE 스트리밍 연결 / SSE streaming connection
             // 업로드된 파일 URL을 imageUrls로 전달 / Pass uploaded file URLs as imageUrls
             const connection = streamKpcTextGeneration(
               prompt,
               uploadedFileUrls, // 업로드된 파일 URL 배열 / Array of uploaded file URLs
               {
          onMessage: (data: string | any) => {
            // 스트리밍 데이터 처리 / Process streaming data
            let textContent = '';
            
            if (typeof data === 'string') {
              // 문자열인 경우 JSON 파싱 시도 / Try to parse JSON if string
              try {
                const parsed = JSON.parse(data);
                if (parsed.type === 'chunk' && parsed.content) {
                  textContent = parsed.content;
                } else if (parsed.type === 'start' || parsed.type === 'end') {
                  return; // 메타데이터 무시 / Ignore metadata
                } else {
                  textContent = data; // JSON이 아닌 경우 그대로 사용
                }
              } catch {
                textContent = data; // JSON 파싱 실패 시 그대로 사용
              }
            } else if (typeof data === 'object' && data !== null) {
              // 객체인 경우 type과 content 확인 / Check type and content for objects
              if (data.type === 'chunk' && data.content) {
                textContent = data.content;
              } else if (data.type === 'start' || data.type === 'end') {
                return; // 메타데이터 무시 / Ignore metadata
              } else {
                textContent = JSON.stringify(data);
              }
            } else {
              textContent = String(data);
            }

            // 빈 내용이면 무시 / Ignore empty content
            if (!textContent.trim()) {
              return;
            }
            
            // AI 메시지는 이미 추가되어 있으므로 스트리밍 상태만 설정 / AI message already added, just set streaming state
            
                   // 스트리밍 데이터를 AI 메시지에 누적 / Accumulate streaming data to AI message
                   flushSync(() => {
                     setMessages(prev =>
                       prev.map(msg =>
                         msg.id === aiMessage.id 
                           ? { ...msg, content: msg.content + textContent } 
                           : msg
                       )
                     );
                   });
                   
                   // 스트리밍 중일 때 스크롤을 하단으로 유지 / Keep scroll at bottom during streaming
                   if (streamingMessageId === aiMessage.id) {
                     // DOM 업데이트 후 정확히 하단으로 스크롤
                     // Scroll to bottom accurately after DOM update
                     requestAnimationFrame(() => {
                       scrollToBottom(false);
                     });
                   }
          },
          onError: (error: Event) => {
            console.error('SSE 스트리밍 오류 / SSE streaming error:', error);
            setError('AI 응답 생성 중 오류가 발생했습니다.');
            setIsLoading(false);
          },
          onComplete: () => {
            console.log('SSE 스트리밍 완료 / SSE streaming completed');
            setIsLoading(false);
            setStreamingMessageId(null); // 스트리밍 완료 시 ID 초기화
            setPrompt('');
            setTranslatedPrompt('');
            setUploadedFileUrls([]);
            setSelectedFiles([]);
          },
          onOpen: () => {
            console.log('SSE 스트리밍 시작 / SSE streaming started');
            // 스트리밍 메시지 ID는 이미 설정되어 있음 / Streaming message ID already set
          },
        }
      );

      setSseConnection(connection);
    } catch (err) {
      console.error('프롬프트 전송 오류 / Send prompt error:', err);
      setError('AI 응답 생성 중 오류가 발생했습니다.');
      setIsLoading(false);
    }
  };

  // 번역된 프롬프트 복사 / Copy translated prompt
  const handleCopyTranslated = () => {
    if (translatedPrompt) {
      navigator.clipboard.writeText(translatedPrompt);
    }
  };

  // 프롬프트 복사 / Copy prompt
  const handleCopyPrompt = () => {
    if (prompt) {
      navigator.clipboard.writeText(prompt);
    }
  };

  const handleCopyMessage = (content: string, messageId: string) => {
    navigator.clipboard.writeText(content);
    setCopiedMessageId(messageId);
    setShowCopySuccess(true); // 복사 성공 알림 표시 / Show copy success notification
    // 2초 후 아이콘을 원래대로 복원
    setTimeout(() => {
      setCopiedMessageId(null);
    }, 2000);
  };

  // 파일 업로드 핸들러 / File upload handler
  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  // 파일 선택 핸들러 / File selection handler
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0]; // 첫 번째 파일만 선택

      // 파일 유효성 검사 / File validation
      const validation = validateFile(file);
      if (!validation.isValid) {
        showSnackbar(validation.error || '파일 업로드에 실패했습니다.');
        return;
      }

      try {
        // 파일 업로드 / Upload file
        setIsUploading(true);
        setError(null); // 이전 에러 클리어
        const uploadedUrl = await uploadFile(file);
        
        // 업로드된 파일 URL 저장 / Save uploaded file URL
        setUploadedFileUrls(prev => [...prev, uploadedUrl]);
        setSelectedFiles([file]);
        
        console.log('파일 업로드 성공 / File upload success:', uploadedUrl);
      } catch (error) {
        console.error('파일 업로드 실패 / File upload failed:', error);
        setError('파일 업로드 중 오류가 발생했습니다.');
      } finally {
        setIsUploading(false);
      }
    }
    
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 파일 삭제 핸들러 / File removal handler
  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setUploadedFileUrls(prev => prev.filter((_, i) => i !== index));
  };

  // 컴포넌트 언마운트 시 SSE 연결 정리 / Clean up SSE connection on unmount
  useEffect(() => {
    return () => {
      if (sseConnection) {
        sseConnection.close();
      }
    };
  }, [sseConnection]);

  return (
    <Box
      sx={{
        height: '75vh',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        position: 'relative', // 절대 위치 요소를 위한 기준점 설정
      }}
    >
      {/* 에러 알림 / Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 메인 채팅 영역 / Main Chat Area */}
      <Paper
        elevation={0}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          // borderRadius: 2,
          // border: '1px solid',
          borderColor: 'divider',
        }}
      >
               {/* 메시지 목록 / Messages List */}
               <Box
                 ref={messagesContainerRef}
                 sx={{
                   flex: 1,
                   overflow: 'auto',
                   p: 2.5,
                   display: 'flex',
                   flexDirection: 'column',
                   gap: 1.25,
                   scrollBehavior: 'smooth', // 부드러운 스크롤
                   '&::-webkit-scrollbar': {
                     width: '6px',
                   },
                   '&::-webkit-scrollbar-track': {
                     background: 'transparent',
                   },
                   '&::-webkit-scrollbar-thumb': {
                     background: 'rgba(0,0,0,0.2)',
                     borderRadius: '3px',
                   },
                   '&::-webkit-scrollbar-thumb:hover': {
                     background: 'rgba(0,0,0,0.3)',
                   },
                 }}
               >
          {messages.length === 0 ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Typography variant="subtitle1" color="text.secondary">
                AI와 대화를 시작해보세요
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                textAlign="center"
              >
                프롬프트를 입력하고 전송 버튼을 눌러 AI 응답을 받아보세요
              </Typography>
            </Box>
          ) : (
            messages.map(message => (
              <Box key={message.id}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: message.type === 'user' ? 'column' : 'row',
                    alignItems:
                      message.type === 'user' ? 'flex-end' : 'flex-start',
                    gap: 0.75,
                    position: 'relative',
                  }}
                >
                  {message.type === 'ai' && (
                    <Avatar
                      className="ai-avatar"
                      sx={{
                        alignSelf: 'flex-start',
                        mt: 1.5,
                      }}
                    >
                      <img
                        src={aiIcon}
                        alt="AI"
                        style={{
                          width: 32,
                          height: 32,
                        }}
                      />
                    </Avatar>
                  )}
                        <Card
                          elevation={0}
                          className={`message-box ${
                            message.type === 'user' ? 'user-message' : 'ai-message'
                          }`}
                        >
                          <CardContent 
                            className="message-content"
                            sx={{
                              minHeight: '2.5em', // 최소 높이 설정으로 레이아웃 안정화
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            {message.type === 'ai' && streamingMessageId === message.id && !message.content ? (
                              <LoadingDots />
                            ) : (
                              <Box sx={{ width: '100%' }}>
                                <ReactMarkdown>{message.content}</ReactMarkdown>
                              </Box>
                            )}
                      {message.translatedContent && (
                        <Box
                          sx={{
                            mt: 1,
                            pt: 1,
                            borderTop: 1,
                            borderColor: 'divider',
                          }}
                        >
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            번역:
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            {message.translatedContent}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                  {message.type === 'user' && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        mt: 0,
                      }}
                    >
                      <Tooltip title="텍스트 복사" arrow placement="top">
                        <IconButton
                          size="small"
                          className="action-button"
                          onClick={() =>
                            handleCopyMessage(message.content, message.id)
                          }
                        >
                          <img
                            src={
                              copiedMessageId === message.id
                                ? normalIcon
                                : copyIcon
                            }
                            alt="Copy"
                            style={{
                              width: 20,
                              height: 20,
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
                {message.type === 'ai' && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      mt: 0,
                      gap: '5px',
                      paddingLeft: '45px',
                    }}
                  >
                    <Tooltip title="텍스트 복사" arrow placement="top">
                      <IconButton
                        size="small"
                        className="action-button"
                        onClick={() =>
                          handleCopyMessage(message.content, message.id)
                        }
                        sx={{ p: 0 }}
                      >
                        <img
                          src={
                            copiedMessageId === message.id
                              ? normalIcon
                              : copyIcon
                          }
                          alt="Copy"
                          style={{
                            width: 20,
                            height: 20,
                            filter: 'brightness(0) saturate(100%)',
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                           <Tooltip title="다시 생성" arrow placement="top">
                             <IconButton
                               size="small"
                               className="action-button"
                               onClick={() => handleRegenerate(message.id)}
                               disabled={isLoading}
                             >
                               <Refresh fontSize="small" sx={{ color: '#000000' }} />
                             </IconButton>
                           </Tooltip>
                  </Box>
                )}
              </Box>
            ))
          )}


          <div ref={messagesEndRef} />
        </Box>

        {/* 입력 영역 / Input Area */}
        <Box sx={{ p: 0 }}>
          <Stack spacing={0}>
            {/* [Gemini/Imagen 전환] 번역 영역 주석 처리 - Gemini는 한국어 지원 */}
            {/* <Collapse in={showTranslation}>
              <Box sx={{ borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Translate fontSize="small" />
                  <Typography variant="subtitle2">번역 결과</Typography>
                  <Box sx={{ flex: 1 }} />
                  <Chip label={`${translatedPrompt.length}/${maxCharacters}자`} size="small" color={translatedPrompt.length > maxCharacters ? 'error' : 'default'} />
                  <Tooltip title="번역 결과 복사">
                    <IconButton size="small" onClick={handleCopyTranslated}><ContentCopy fontSize="small" /></IconButton>
                  </Tooltip>
                </Box>
                <TextField fullWidth multiline rows={2} value={translatedPrompt} placeholder="번역된 프롬프트가 여기에 표시됩니다..." variant="outlined" InputProps={{ readOnly: true }} />
              </Box>
            </Collapse>
            {showTranslation && <Divider sx={{ my: 2 }} />} */}

            {/* 메인 입력창 / Main Input - hideInput이 false일 때만 표시 */}
            {!hideInput && (
              <>
                {/* 파일 업로드 상태 표시 / File upload status display */}
                {isUploading && (
                  <Box sx={{ px: 1.5, mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LoadingDots />
                      <Typography variant="caption" color="text.secondary">
                        파일 업로드 중... / Uploading file...
                      </Typography>
                    </Box>
                  </Box>
                )}


                {/* 문자 카운터 - 입력창 위 div / Character counter - Above input div */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mb: 1,
                    px: 1.5,
                  }}
                >
                  <CharacterCounter
                    currentCount={prompt.length}
                    configType="text"
                    label="자"
                  />
                </Box>
                {/* 숨겨진 파일 입력 / Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,.csv,.json,.xml,.png,.jpeg,.jpg"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <PromptComposer
                  value={prompt}
                  onChange={setPrompt}
                  onSend={handleSendPrompt}
                  isSending={isLoading}
                  maxCharacters={maxCharacters}
                  placeholder="AI에게 질문하거나 요청사항을 입력하세요."
                  leftAction={{ 
                    type: 'file', 
                    onClick: handleFileUpload
                  }}
                  showCharacterCount={false}
                  selectedFiles={selectedFiles}
                  onRemoveFile={handleRemoveFile}
                  rightActions={
                    <Stack direction="row" spacing={1} sx={{ mb: 0.25 }}>
                      {/* [Gemini/Imagen 전환] 번역 버튼 주석 처리 - Gemini는 한국어 지원
                      <Button
                        variant="outlined"
                        startIcon={<Translate />}
                        onClick={handleTranslate}
                        disabled={!prompt.trim() || isTranslating}
                        size="small"
                        sx={{ minHeight: '24px' }}
                      >
                        {isTranslating ? '번역중' : '번역'}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => setShowTranslation(!showTranslation)}
                        size="small"
                        sx={{ minHeight: '24px' }}
                      >
                        {showTranslation ? '숨기기' : '보기'}
                      </Button>
                      */}
                      <Tooltip title="프롬프트 복사">
                        <IconButton size="small" onClick={handleCopyPrompt}>
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  }
                />
              </>
            )}
          </Stack>
        </Box>
      </Paper>

            {/* 모달 컴포넌트들 / Modal Components */}
            <TextGenerationModals
            /* showSettingsModal, setShowSettingsModal props 제거됨 / removed */
            />

            {/* 복사 성공 알림 / Copy Success Notification */}
            <Snackbar
              open={showCopySuccess}
              autoHideDuration={2000}
              onClose={handleCloseCopySuccess}
              message="텍스트가 복사되었습니다"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />

            {/* Snackbar / Snackbar */}
            <Snackbar
              open={snackbarOpen}
              autoHideDuration={4000}
              onClose={() => setSnackbarOpen(false)}
              message={snackbarMessage}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
          </Box>
        );
      };

      export default TextGenerationTab;
